import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { PlusIcon } from '@phosphor-icons/react/dist/csr/Plus';

import { EscalationFormDialog } from './EscalationFormDialog';
import { QUEUE_EMPTY_KEY, QUEUE_LABEL_KEY, STATUS_LABEL_KEY } from './escalationLabels';
import { PageActions } from '../../app/shell/PageActions';
import { FilterBar } from '../../design-system/composites/FilterBar/FilterBar';
import { Button } from '../../design-system/primitives/Button/Button';
import {
  DataTable,
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import { Pagination } from '../../design-system/composites/Pagination/Pagination';
import { useCursorPages } from '../../design-system/composites/Pagination/useCursorPages';
import { Input } from '../../design-system/primitives/Input/Input';
import { SegmentedControl } from '../../design-system/primitives/SegmentedControl/SegmentedControl';
import { ExportButton } from '../shared/ExportButton';
import { ApiError } from '../../lib/api/client';
import {
  escalationsApi,
  type EscalationQuery,
  type EscalationRow,
  type EscalationStatusFilter,
} from '../../lib/api/escalations';
import { formatTimestamp } from '../../lib/format/dateTime';
import { canReadEscalations } from '../../lib/permissions/escalations';
import styles from './EscalationList.module.css';

/**
 * The escalations queue (M-10, Task 5, spec §3) — `/escalations`, a nav
 * entry that has never pointed anywhere before this milestone (the legacy
 * hid the whole feature by commenting out its sidebar links; spec §0 says
 * so is not a decision this milestone repeats). Copies `RequestList.tsx`'s
 * own structure — the most recently reviewed screen, carrying the fixes
 * M-09 paid for: a segmented control over the server's own status filter, a
 * draft/applied filter row, a cursor-paginated table.
 *
 * ## `status` travels to the server exactly as `mine`/`case_number`/`mobile` do
 *
 * `escalationsApi.list` does the filtering — this component never narrows a
 * broader response client-side. `status: 'all'` is one of the FOUR values
 * `EscalationIndexRequest::rules()` accepts, the value `CaseEscalations.tsx`
 * (the case-detail panel) passes explicitly for the identical reason
 * `CaseRequests.tsx` documents at length: the segmented control's own
 * default (`open`) would otherwise hide every escalation this SCREEN's own
 * filters do not ask for, which is a defect for a work-list queue but would
 * be a defect for a case-scoped panel that must show everything.
 */

export interface EscalationListProps {
  permissions: readonly string[];
  onOpen: (uuid: string) => void;
  /**
   * Whether this list is the view on screen. The screen keeps the list
   * mounted (hidden) under an opened record, and `PageActions` portals into
   * the shell's title row whatever `hidden` says — so a hidden list withdraws
   * its page commands, or they would sit beside the detail.
   */
  active?: boolean;
}

interface EscalationListRow extends Row, EscalationRow {}

function toRow(node: EscalationRow): EscalationListRow {
  return { ...node, id: node.uuid };
}

const QUEUES: readonly EscalationStatusFilter[] = ['open', 'resolved', 'closed', 'all'];

interface FilterState {
  caseNumber: string;
  mobile: string;
  mine: boolean;
}

const BLANK_FILTERS: FilterState = { caseNumber: '', mobile: '', mine: false };

function toQuery(
  status: EscalationStatusFilter,
  filters: FilterState,
  cursor?: string,
  perPage?: number,
): EscalationQuery {
  const query: EscalationQuery = { status };
  if (filters.caseNumber.trim() !== '') query.case_number = filters.caseNumber.trim();
  if (filters.mobile.trim() !== '') query.mobile = filters.mobile.trim();
  if (filters.mine) query.mine = true;
  if (cursor !== undefined) query.cursor = cursor;
  if (perPage !== undefined) query.per_page = perPage;
  return query;
}

/** Read off the OUTGOING QUERY, not `appliedFilters` state — `RequestList.tsx`'s own `hasAppliedFilters` shape. `status` is excluded: it always travels. */
function hasAppliedFilters(query: EscalationQuery): boolean {
  return query.case_number !== undefined || query.mobile !== undefined || query.mine !== undefined;
}

export function EscalationList({ permissions, onOpen, active = true }: EscalationListProps) {
  const { t, i18n } = useTranslation();
  const canCreate = permissions.includes('escalation.create');

  const [statusFilter, setStatusFilter] = useState<EscalationStatusFilter>('open');
  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly EscalationListRow[]>([]);
  const [reference, setReference] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  // 50 — `EscalationIndexRequest::DEFAULT_PER_PAGE` server-side.
  const pages = useCursorPages();
  // Destructured — `pages` is a fresh object every render; see
  // `CaseList.tsx`'s identical note.
  const { observe } = pages;
  const [draftFilters, setDraftFilters] = useState<FilterState>(BLANK_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(BLANK_FILTERS);
  const [formOpen, setFormOpen] = useState(false);

  const requestSeq = useRef(0);

  const load = useCallback(async (query: EscalationQuery) => {
    const seq = ++requestSeq.current;
    setStatus('loading');
    setReference(null);

    try {
      const page = await escalationsApi.list(query);
      if (seq !== requestSeq.current) return;
      setRows(page.data.map(toRow));
      setTotal(page.meta.total);
      observe({ next_cursor: page.meta.next_cursor, total: page.meta.total });
      setStatus(page.data.length === 0 ? (hasAppliedFilters(query) ? 'no-results' : 'empty') : 'ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (seq !== requestSeq.current) return;
      setReference(error.requestId ?? null);
      setStatus('error');
    }
  }, [observe]);

  useEffect(() => {
    void load(toQuery(statusFilter, appliedFilters, pages.cursor ?? undefined, pages.perPage));
  }, [load, statusFilter, appliedFilters, pages.cursor, pages.perPage]);

  function handleStatusChange(value: string): void {
    setStatusFilter(value as EscalationStatusFilter);
    pages.first();
  }

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    pages.first();
    setAppliedFilters(draftFilters);
  }

  function handleClearFilters(): void {
    setDraftFilters(BLANK_FILTERS);
    pages.first();
    setAppliedFilters(BLANK_FILTERS);
  }

  function retry(): void {
    void load(toQuery(statusFilter, appliedFilters, pages.cursor ?? undefined, pages.perPage));
  }

  function updateDraft(field: 'caseNumber' | 'mobile') {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setDraftFilters((current) => ({ ...current, [field]: value }));
    };
  }

  // A freshly created escalation is ALWAYS `status: 'open'`
  // (`EscalationController::store()`) — prepending it unconditionally put an
  // open row into whichever queue happened to be selected, including
  // "Resolved" and "Closed" (whole-branch review, M-1). It belongs only in
  // the queues that would ever show an open row: "Open" itself and "All".
  function handleCreated(created: EscalationRow): void {
    if (statusFilter === 'open' || statusFilter === 'all') {
      setRows((current) => [toRow(created), ...current]);
      if (status === 'empty') setStatus('ready');
    }
    setFormOpen(false);
  }

  function renderCaseNumber(row: Row): ReactNode {
    return (row as unknown as EscalationRow).case?.case_number ?? (row as unknown as EscalationRow).case_number;
  }

  function renderLevel(row: Row): ReactNode {
    return (row as unknown as EscalationRow).level;
  }

  function renderStatus(row: Row): ReactNode {
    return t(STATUS_LABEL_KEY[(row as unknown as EscalationRow).status]);
  }

  function renderAssignee(row: Row): ReactNode {
    return (row as unknown as EscalationRow).assignee_name;
  }

  function renderOpenedAt(row: Row): ReactNode {
    const value = (row as unknown as EscalationRow).opened_at;
    return value === null ? null : formatTimestamp(value, i18n.language);
  }

  const queueOptions = QUEUES.map((value) => ({ value, label: t(QUEUE_LABEL_KEY[value]) }));

  const columns: readonly Column[] = [
    { key: 'mobile', label: t('table.mobile'), neverMirrors: true },
    { key: 'case_number', label: t('table.caseNumber'), neverMirrors: true, render: renderCaseNumber },
    { key: 'level', label: t('escalations.level'), numeric: true, render: renderLevel },
    { key: 'status', label: t('table.status'), render: renderStatus },
    { key: 'assignee_name', label: t('escalations.assignedTo'), render: renderAssignee },
    { key: 'opened_at', label: t('escalations.openedAt'), render: renderOpenedAt },
  ];

  return (
    <div className={styles['page']}>
      {/* No second heading under the shell's `<h1>`: "New escalation" and
          Export sit in the title row. `statusFilter` travels with the export
          filters — it always reaches the wire (see `hasAppliedFilters`), so
          the file is the queue on screen. No cursor: an export is the whole
          filtered set. */}
      {active ? (
        <PageActions>
          {canCreate ? (
            <Button
              onClick={() => {
                setFormOpen(true);
              }}
              icon={<PlusIcon size={16} />}
            >
              {t('escalations.newEscalation')}
            </Button>
          ) : null}
          <ExportButton
            module="escalations"
            filters={{ ...toQuery(statusFilter, appliedFilters) }}
            canExport={canReadEscalations(permissions)}
          />
        </PageActions>
      ) : null}

      <FilterBar
        onSubmit={handleFilterSubmit}
        onClear={handleClearFilters}
        leading={
          <SegmentedControl
            label={t('escalations.queueLabel')}
            options={queueOptions}
            value={statusFilter}
            onChange={handleStatusChange}
          />
        }
      >
        <Input
          label={t('table.caseNumber')}
          width="compact"
          dir="ltr"
          value={draftFilters.caseNumber}
          onChange={updateDraft('caseNumber')}
        />
        <Input
          label={t('table.mobile')}
          width="phone"
          dir="ltr"
          value={draftFilters.mobile}
          onChange={updateDraft('mobile')}
        />

        <label className={styles['mineOption']}>
          <input
            type="checkbox"
            checked={draftFilters.mine}
            onChange={(event) => {
              const { checked } = event.target;
              setDraftFilters((current) => ({ ...current, mine: checked }));
            }}
          />
          {t('escalations.showMine')}
        </label>
      </FilterBar>

      <DataTable
        caption={t('escalations.tableCaption')}
        columns={columns}
        rows={rows}
        status={status}
        empty={{ body: t(QUEUE_EMPTY_KEY[statusFilter]) }}
        noResults={{ title: t('list.noResultsTitle') }}
        onClearFilters={handleClearFilters}
        error={{ title: t('escalations.queueLoadFailed') }}
        onRowActivate={(row) => {
          onOpen(row.id);
        }}
      />

      <Pagination pages={pages} total={total} rowsOnPage={rows.length} />

      {status === 'error' ? (
        <div className={styles['recovery']}>
          <Button variant="secondary" onClick={retry}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('actions.retry')}
          </Button>

          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="escalationListRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      ) : null}

      <EscalationFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
        }}
        onSaved={handleCreated}
      />
    </div>
  );
}
